<script setup lang="ts">
import {
    DelayFeedback,
    Feedback,
    ProgramSnapshot,
    SupportProgram,
    SupportProgramMeta,
    SupportProgramPatient
} from "@/program/interfaces/Program";
import dayjs from "dayjs";
import Markdown from "@/core/components/markdown/Markdown.vue";
import SupportProgramPurchaseHistory from "@/program/components/support-program/SupportProgramPurchaseHistory.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import SupportProgramMention from "@/program/components/support-program/SupportProgramMention.vue";
import {computed, ComputedRef, onMounted, reactive, watch} from "vue";
import {useToastStore} from "@/stores/toast.store";
import {programApi} from "@/container";
import {Button} from "@/core/components/ui/button";
import {Badge} from "@/core/components/ui/badge";
import ProgramFeedbackDialog from "@/program/components/program-feedback-dialog/ProgramFeedbackDialog.vue";
import {EyeOff, History, RotateCcw, Target} from "@lucide/vue";

const {showError, showSuccess} = useToastStore();
const props = defineProps<{
    consumerKey: string;
    program: ProgramSnapshot;
}>();

const emits = defineEmits(["delayed", "rejected", "canceled"]);

const state = reactive({
    loadingHistory: false,
    loadedHistory: null as { data: SupportProgramPatient, meta: SupportProgramMeta } | null,
    loadingReject: false,
    loadingCancel: false,
    consumerDisabled: false
});

watch(() => props.program.feedbacks, (newFeedbacks) => {
    state.consumerDisabled = !!newFeedbacks?.find(feedback => feedback.hide_notification);
}, {immediate: true});

const supportProgram: ComputedRef<SupportProgram> = computed(() => {
    return props.program.programmable as SupportProgram;
});

/**
 * The consumer sale history, always loaded from the history endpoint: the
 * sales embedded in the program snapshot are capped by the groups' store_sales
 * count and would render a truncated history.
 */
const salesHistory = computed(() => state.loadedHistory);

onMounted(() => {
    if (props.program.meta?.options?.autoloadSales) {
        loadSaleHistory(props.program.programmable_id, props.consumerKey);
    }
});

/**
 * Load the consumer sale history for the support program.
 */
const loadSaleHistory = async (supportProgramId: number, consumerKey: string) => {
    state.loadingHistory = true;

    try {
        state.loadedHistory = await programApi().history(supportProgramId, consumerKey);
    } catch {
        showError({content: "Une erreur est survenue lors du chargement de l'historique des achats."});
    } finally {
        state.loadingHistory = false;
    }
};

const handleRejected = async () => {
    state.consumerDisabled = true;
    window.ipcRenderer.hideProgram(props.consumerKey, props.program.id);
    showSuccess({
        content: "Le programme ne sera plus affiché pour ce patient."
    });
    emits("rejected");
};

const handleDelayed = () => {
    state.consumerDisabled = true;
    showSuccess({
        content: "Le programme ne sera plus affiché pour ce patient."
    });
    emits("delayed");
};

/**
 * The feedback currently hiding the program for this consumer, if any.
 */
const hidingFeedback: ComputedRef<Feedback | undefined> = computed(() => {
    return props.program.feedbacks?.find(feedback => feedback.hide_notification);
});

/**
 * The hiding duration label, only available for delay feedbacks.
 */
const hidingDurationLabel: ComputedRef<string | null> = computed(() => {
    if (hidingFeedback.value?.type !== "delay") {
        return null;
    }

    const delay = hidingFeedback.value.feedbackable as DelayFeedback;
    const until = dayjs(delay.delayed_at).add(delay.days, "day").format("DD/MM/YYYY");

    return `Masqué pendant ${delay.days} jour${delay.days > 1 ? "s" : ""}, jusqu'au ${until}.`;
});

/**
 * Cancel the feedback hiding the program, so it is displayed again for this consumer.
 */
const cancelHiding = async () => {
    if (!hidingFeedback.value) {
        return;
    }

    state.loadingCancel = true;

    try {
        await programApi().deleteFeedback(props.program.id, hidingFeedback.value.id);
        state.consumerDisabled = false;
        showSuccess({
            content: "Le programme sera de nouveau affiché pour ce patient."
        });
        emits("canceled");
    } catch {
        showError({content: "Une erreur est survenue lors de l'annulation du masquage."});
    } finally {
        state.loadingCancel = false;
    }
};
</script>

<template>
  <div class="space-y-4">
    <div
      v-if="!!program.additional_information?.length"
      class="p-5 bg-white rounded-xl shadow-sm ring-1 ring-slate-100"
    >
      <markdown :content="program.additional_information" inherit-styles />
    </div>

    <div class="p-5 bg-white rounded-xl shadow-sm ring-1 ring-slate-100 space-y-4">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="space-y-3 flex-1">
          <div class="flex items-center gap-2">
            <div class="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Target class="size-4" />
            </div>
            <h3 class="text-base font-semibold text-slate-900">
              Pourquoi le patient est ciblé ?
            </h3>
          </div>
          <div class="flex flex-wrap gap-2 items-center">
            <Badge
              v-for="group in program.groups.included"
              :key="group.slug"
              variant="secondary"
              class="px-3 py-1 text-sm font-normal"
            >
              {{ program.meta.included[group.slug] || group.slug }}
            </Badge>
            <Button
              v-if="!salesHistory"
              variant="link"
              size="sm"
              class="text-primary h-auto px-1"
              :disabled="state.loadingHistory || !!state.loadedHistory"
              @click="loadSaleHistory(program.programmable_id, consumerKey)"
            >
              <History class="size-4" />
              Voir l'historique des achats
              <BaseFlexSpinner v-if="state.loadingHistory" size="button" />
            </Button>
          </div>
        </div>
        <ProgramFeedbackDialog
          v-if="!state.consumerDisabled"
          class="shrink-0"
          :program-id="program.id"
          :consumer-key="consumerKey"
          @delayed="handleDelayed"
          @rejected="handleRejected"
        />
      </div>

      <div
        v-if="state.consumerDisabled"
        class="flex flex-col gap-3 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-100 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex items-start gap-3">
          <div class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-200/70 text-slate-500">
            <EyeOff class="size-4" />
          </div>
          <div class="space-y-0.5">
            <p class="text-sm font-medium text-slate-700">
              Ce programme n'est plus affiché pour ce patient.
            </p>
            <p v-if="hidingDurationLabel" class="text-sm text-slate-500">
              {{ hidingDurationLabel }}
            </p>
          </div>
        </div>
        <Button
          v-if="hidingFeedback"
          variant="outline"
          size="sm"
          class="shrink-0"
          :disabled="state.loadingCancel"
          @click="cancelHiding"
        >
          <RotateCcw class="size-4" />
          Annuler le masquage
          <BaseFlexSpinner v-if="state.loadingCancel" size="button" />
        </Button>
      </div>
    </div>

    <SupportProgramPurchaseHistory
      v-if="salesHistory"
      :history="salesHistory"
    />
    <div v-if="supportProgram.terms">
      <SupportProgramMention :mentions="supportProgram.terms" />
    </div>
  </div>
</template>

<style scoped>

</style>
